<template>
    <component
        :is="markRaw(component)"
        :series="series"
        :diagram="diagram"
        :parts="filteredResults"
        :finishes="finishOptions"
        :is-loading="isLoading"
        :query="query"
        :sort="sort"
        @search="handleSearch"
        @sort="handleSort"
        @filter:finish="handleFilterFinish"
        @filter:legend="handleFilterLegend"
    />
</template>
<script setup>
import { computed, markRaw, onMounted, ref } from "vue";
import { eventBus, useUser } from "@/Composables";
import MarketingControls from "@/Themes/Marketing/Components/Modules/Parts/PartsBySeries.vue";
import SourcingControls from "@/Themes/Sourcing/Components/Modules/Parts/PartsBySeries.vue";
import _ from "lodash";

const { series } = defineProps({
    series: {
        type: Object,
        required: true,
    },
})

const { settings } = useUser();
const component = computed(() => settings.value.is_sourcing_mode ? SourcingControls : MarketingControls);
const isLoading = ref(true);
const sort = ref({})
const query = ref('');
const parts = ref([])
const diagram = ref(null)
const legendKey = ref(null)
const finish = ref(null)

const sanitizedQuery = computed(() => {
    return query.value.trim()
})
const filteredResults = computed(() => {
    // Filter the results
    let results = parts.value.slice()
        .map(row => _.cloneDeep(row))
        .filter(row => !legendKey.value || row.legend_key === legendKey.value)
        .filter(row => !finish.value || row.facet_finish?.includes(finish.value))
        .filter(row => {
            if (!sanitizedQuery.value) {
                return true;
            }

            const query = sanitizedQuery.value.toLowerCase();
            let isMatchSku = row.item_id?.toLowerCase().includes(query);
            let isMatchDescription = row.display_short_description?.toLowerCase().includes(query);

            return isMatchSku || isMatchDescription
        });

    // Apply sorting if sort criteria is set
    if (sort.value && Object.keys(sort.value).length > 0) {
        const [column, order] = Object.entries(sort.value)[0];
        results.sort((a, b) => {
            if (a[column] < b[column]) return order === 'asc' ? -1 : 1;
            if (a[column] > b[column]) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }

    return results
})

const finishOptions = computed(() => {
    const options = parts.value.slice().reduce((options, part) => options.concat(part.facet_finish), [])

    return [...new Set(options)].filter(Boolean).sort()
})

function handleSearch(input) {
    query.value = input
}

function handleSort(value) {
    sort.value = { ...value };
}

function handleFilterLegend(value) {
    legendKey.value = value ? value : null;
}

function handleFilterFinish(value) {
    finish.value = value ? value : null;
}

onMounted(() => {
    axios
        .get(route('web.parts.show', series))
        .then(response => {
            diagram.value = response.data.diagram
            parts.value = response.data.parts
        })
        .catch(error => {
            eventBus.emit("alert:show", {
                title: "Error Loading Parts",
                level: "danger",
            });
        })
        .finally(() => {
            isLoading.value = false
        })
})
</script>
